import React, { useState, useRef } from 'react';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useNavigate, Link } from 'react-router-dom';
import { doc, setDoc } from 'firebase/firestore';
import ReCAPTCHA from 'react-google-recaptcha';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

// Component to visualize the strength of the user's password.
// It provides real-time feedback to encourage stronger passwords.
const PasswordStrengthIndicator = ({ password }) => {
    // Calculates a strength score for the given password.
    // The score is based on length, and inclusion of different character types.
    const getStrength = (password) => {
        let strength = 0;
        if (!password) return strength;

        // Award points for various password criteria.
        if (password.length >= 8) strength += 1; // Minimum length
        if (password.match(/[a-z]/)) strength += 1; // Lowercase letters
        if (password.match(/[A-Z]/)) strength += 1; // Uppercase letters
        if (password.match(/[0-9]/)) strength += 1; // Numbers
        if (password.match(/[^a-zA-Z0-9]/)) strength += 1; // Special characters
        
        return strength;
    };

    const strength = getStrength(password);
    // Labels and colors corresponding to the strength score.
    const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
    const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];

    return (
        <div className="flex items-center mt-2 space-x-2" aria-live="polite">
            {/* The progress bar representing password strength */}
            <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                    className={`h-2 rounded-full transition-all duration-300 ${strengthColors[strength - 1] || 'bg-gray-200'}`} 
                    style={{ width: `${(strength / 5) * 100}%` }}
                ></div>
            </div>
            {/* Text label for the current strength level */}
            <span className="text-sm text-gray-600 w-20 text-right">{strength > 0 ? strengthLabels[strength - 1] : ''}</span>
        </div>
    );
};


// The main registration component.
function Register({ loadingBarRef }) {
    // State to hold all form data in a single object for easier management.
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
    });

    // State to hold validation errors for each form field.
    const [errors, setErrors] = useState({});
    // State to manage the visibility of the password field.
    const [showPassword, setShowPassword] = useState(false);
    // State to manage the visibility of the confirm password field.
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    // State for the ReCAPTCHA value, confirming the user is not a bot.
    const [captchaValue, setCaptchaValue] = useState(null);
    // State to manage the submission process, e.g., for showing a loading state on the button.
    const [isSubmitting, setIsSubmitting] = useState(false);
    // State for displaying errors from the server (e.g., Firebase, network issues).
    const [serverError, setServerError] = useState('');

    // React Router's navigation function for redirecting the user after registration.
    const navigate = useNavigate();
    // A ref for the ReCAPTCHA component, allowing for manual actions like resetting it.
    const recaptchaRef = useRef();

    // Validates the entire form and updates the errors state.
    // Returns true if the form is valid, otherwise false.
    const validateForm = () => {
        const newErrors = {};
        
        // Name validation: must not be empty and should contain a space.
        if (!formData.name.trim()) {
            newErrors.name = 'Full name is required.';
        } else if (!formData.name.trim().includes(' ')) {
            newErrors.name = 'Please enter your full name (first and last).';
        }

        // Email validation: must not be empty and must be in a valid format.
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required.';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address.';
        }

        // Phone validation: must be a 10-digit Indian mobile number.
        const phoneRegex = /^[6-9]\d{9}$/;
        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required.';
        } else if (!phoneRegex.test(formData.phone)) {
            newErrors.phone = 'Please enter a valid 10-digit Indian mobile number.';
        }

        // Password validation: must be at least 8 characters long.
        if (!formData.password) {
            newErrors.password = 'Password is required.';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters long.';
        }
        
        // Confirm Password validation: must match the password field.
        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match.';
        }

        setErrors(newErrors);
        // The form is considered valid if the newErrors object has no keys.
        return Object.keys(newErrors).length === 0;
    };

    // Handles changes in form inputs, updating the formData state.
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear the specific error message for a field when the user starts typing in it.
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    // The main function to handle the registration logic on form submission.
    const handleRegister = async (e) => {
        e.preventDefault(); // Prevents the browser's default form submission behavior.
        setServerError(''); // Clears any previous server errors on a new submission attempt.

        // First, perform client-side validation.
        if (!validateForm()) {
            return; // If validation fails, stop the submission process.
        }

        // Check if the ReCAPTCHA has been completed.
        if (!captchaValue) {
            setServerError("Please complete the captcha to verify you're not a robot.");
            return;
        }

        setIsSubmitting(true); // Set submitting state to true to show loading indicators.
        // If a loading bar is provided, start it to indicate progress.
        if (loadingBarRef.current) {
            loadingBarRef.current.continuousStart();
        }

        try {
            // Use Firebase Authentication to create a new user with email and password.
            const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
            
            // If user creation is successful, save additional user information to Firestore.
            await setDoc(doc(db, 'users', userCredential.user.uid), {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                userType: 'User', // Assign a default user type.
                createdAt: new Date().toISOString(),
                // It's good practice to store metadata about the registration event.
                metadata: {
                    registrationSource: 'web-register-page',
                    userAgent: navigator.userAgent,
                    platform: navigator.platform,
                }
            });

            // If the loading bar exists, mark the process as complete.
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
            
            // Redirect the user to the home page upon successful registration.
            navigate('/');

        } catch (err) {
            // Stop the loading bar if an error occurs.
            if (loadingBarRef.current) {
                loadingBarRef.current.complete();
            }
            
            // Provide user-friendly error messages based on the error code from Firebase.
            if (err.code === 'auth/email-already-in-use') {
                setServerError('This email is already registered. Please try to login instead.');
                setErrors(prev => ({ ...prev, email: 'This email is already in use.' }));
            } else {
                setServerError(err.message || 'An unexpected error occurred. Please try again.');
            }
            
            // Reset the ReCAPTCHA to allow the user to attempt submission again.
            recaptchaRef.current.reset();
            setCaptchaValue(null);
        } finally {
            // Reset the submitting state regardless of success or failure.
            setIsSubmitting(false);
        }
    };

    // The JSX for rendering the registration form.
    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                {/* Application Logo */}
                <img className="mx-auto h-12 w-auto" src="/logo.png" alt="YXC" />
                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                    Create your account
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600">
                    Or{' '}
                    <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
                        sign in to your existing account
                    </Link>
                </p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
                    <form onSubmit={handleRegister} className="space-y-6" noValidate>
                        {/* A container to display server-side errors */}
                        {serverError && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-sm text-red-700">{serverError}</p>
                            </div>
                        )}
                        
                        {/* Full Name Input Field */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                Full Name
                            </label>
                            <div className="mt-1">
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    autoComplete="name"
                                    required
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                                        errors.name ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                                {errors.name && <p className="mt-2 text-sm text-red-600">{errors.name}</p>}
                            </div>
                        </div>

                        {/* Email Input Field */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                Email address
                            </label>
                            <div className="mt-1">
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                                        errors.email ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                                {errors.email && <p className="mt-2 text-sm text-red-600">{errors.email}</p>}
                            </div>
                        </div>

                        {/* Phone Number Input Field */}
                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                Phone Number
                            </label>
                            <div className="mt-1">
                                <input
                                    id="phone"
                                    name="phone"
                                    type="tel"
                                    autoComplete="tel"
                                    required
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                                        errors.phone ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                                {errors.phone && <p className="mt-2 text-sm text-red-600">{errors.phone}</p>}
                            </div>
                        </div>

                        {/* Password Input Field with visibility toggle */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                Password
                            </label>
                            <div className="mt-1 relative">
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    required
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                                        errors.password ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500">
                                    {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                </button>
                            </div>
                            {errors.password && <p className="mt-2 text-sm text-red-600">{errors.password}</p>}
                            {formData.password && <PasswordStrengthIndicator password={formData.password} />}
                        </div>
                        
                        {/* Confirm Password Input Field with visibility toggle */}
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                Confirm Password
                            </label>
                            <div className="mt-1 relative">
                                <input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    required
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    className={`appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                                        errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                />
                                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500">
                                    {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                </button>
                            </div>
                            {errors.confirmPassword && <p className="mt-2 text-sm text-red-600">{errors.confirmPassword}</p>}
                        </div>

                        {/* Google ReCAPTCHA Component */}
                        <div className="flex justify-center">
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey="6Leys3EqAAAAAEjkHcbcAjaoJ1Bf4o1xcG8W1Hv9"
                                onChange={(value) => setCaptchaValue(value)}
                                theme="light" // Explicitly set theme for consistency
                            />
                        </div>

                        {/* Form Submission Button */}
                        <div>
                            <button
                                type="submit"
                                disabled={isSubmitting || !captchaValue}
                                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-400 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? (
                                    <>
                                        {/* Loading spinner shown during submission */}
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Registering...
                                    </>
                                ) : (
                                    'Create Account'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default Register;
